import { ReactNode } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  ReferenceLine, LineChart, Line, Legend, Cell,
} from 'recharts';
import { fmtCurrency, fmtMonth } from '../lib/fmt';

// Chart palette — brand accents for primary categorical bars, functional
// semantics for ranges/status. Kept small so charts stay cohesive across the
// app. Extend only when a new axis of meaning comes up.
export const CHART_COLORS = {
  shipped:      'var(--color-green)',
  wip:          'var(--color-amber)',
  deliverable:  'var(--color-brand-red)',
  overhead:     'var(--color-brand-yellow)',
  neutral:      'var(--color-text-muted)',
  axis:         'var(--color-text-muted)',
  grid:         'var(--color-border)',
  tooltip_bg:   'var(--color-surface)',
  tier_line:    'var(--color-blue)',
} as const;

// ─── Tooltip ──────────────────────────────────────────────────────────────────

interface TooltipPayloadEntry { name?: string; value?: number | string; color?: string; dataKey?: string }

function CurrencyTooltip({ active, payload, label }: { active?: boolean; payload?: TooltipPayloadEntry[]; label?: string }) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div style={{
      background: 'var(--color-surface)',
      border: '0.5px solid var(--color-border-strong)',
      borderRadius: 'var(--radius-sm)',
      padding: '6px 10px',
      fontSize: 11,
      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    }}>
      <div style={{ fontWeight: 500, marginBottom: 2 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color ?? 'var(--color-text)', display: 'flex', gap: 6 }}>
          <span style={{ color: 'var(--color-text-muted)' }}>{p.name}:</span>
          <strong>{typeof p.value === 'number' ? fmtCurrency(p.value) : p.value}</strong>
        </div>
      ))}
    </div>
  );
}

// ─── Chart card wrapper ──────────────────────────────────────────────────────

export function ChartCard({ title, sub, height = 220, children }: {
  title: string;
  sub?: ReactNode;
  height?: number;
  children: ReactNode;
}) {
  return (
    <div style={{
      background: 'var(--color-surface)',
      border: '0.5px solid var(--color-border)',
      borderRadius: 'var(--radius-lg)',
      padding: '12px 16px',
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 500 }}>{title}</div>
        {sub && <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{sub}</div>}
      </div>
      <div style={{ width: '100%', height }}>{children}</div>
    </div>
  );
}

// ─── Shipped-value trend (Scorecard history) ─────────────────────────────────

export function ShippedTrendChart({
  periods,
  tiers,
}: {
  periods: Array<{ attribution_month: string; shipped_value: number; wip_value: number }>;
  tiers: Array<{ tier: number; threshold_amount: number }>;
}) {
  const data = [...periods]
    .reverse()
    .map((p) => ({
      month: fmtMonth(p.attribution_month).replace(' ', '\n'),
      Shipped: Math.round(p.shipped_value),
      WIP: Math.round(p.wip_value),
    }));

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={{ top: 8, right: 10, left: 0, bottom: 0 }}>
        <CartesianGrid stroke={CHART_COLORS.grid} vertical={false} />
        <XAxis
          dataKey="month"
          tick={{ fontSize: 10, fill: CHART_COLORS.axis }}
          stroke={CHART_COLORS.grid}
          interval={0}
        />
        <YAxis
          tick={{ fontSize: 10, fill: CHART_COLORS.axis }}
          stroke={CHART_COLORS.grid}
          tickFormatter={(v) => `$${Math.round(Number(v) / 1000)}k`}
        />
        <Tooltip content={<CurrencyTooltip />} />
        {tiers.map((t) => (
          <ReferenceLine
            key={t.tier}
            y={t.threshold_amount}
            stroke={CHART_COLORS.tier_line}
            strokeDasharray="3 3"
            strokeOpacity={0.4}
            label={{ value: `T${t.tier}`, position: 'right', fontSize: 9, fill: CHART_COLORS.tier_line, opacity: 0.7 }}
          />
        ))}
        <Legend wrapperStyle={{ fontSize: 10 }} />
        <Bar dataKey="Shipped" fill={CHART_COLORS.shipped} radius={[3, 3, 0, 0]} />
        <Bar dataKey="WIP" fill={CHART_COLORS.wip} radius={[3, 3, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

// ─── Tier-progress bar (horizontal) — current period ─────────────────────────

export function TierProgressBar({
  value,
  tiers,
}: {
  value: number;
  tiers: Array<{ tier: number; threshold_amount: number; multiplier: number }>;
}) {
  const max = Math.max(
    value,
    tiers[tiers.length - 1]?.threshold_amount ?? 1,
  );
  const data = [{ name: 'Shipped', value: Math.round(value) }];

  return (
    <div style={{ width: '100%', height: 70 }}>
      <ResponsiveContainer>
        <BarChart data={data} layout="vertical" margin={{ top: 8, right: 10, left: 0, bottom: 0 }}>
          <XAxis
            type="number"
            domain={[0, max * 1.05]}
            tick={{ fontSize: 10, fill: CHART_COLORS.axis }}
            stroke={CHART_COLORS.grid}
            tickFormatter={(v) => `$${Math.round(Number(v) / 1000)}k`}
          />
          <YAxis type="category" dataKey="name" hide />
          <Tooltip content={<CurrencyTooltip />} />
          {tiers.map((t) => (
            <ReferenceLine
              key={t.tier}
              x={t.threshold_amount}
              stroke={CHART_COLORS.tier_line}
              strokeDasharray="3 3"
              strokeOpacity={0.5}
              label={{ value: `T${t.tier} · ${(t.multiplier * 100).toFixed(1)}%`, position: 'top', fontSize: 9, fill: CHART_COLORS.tier_line }}
            />
          ))}
          <Bar dataKey="value" fill={CHART_COLORS.shipped} radius={[0, 4, 4, 0]}>
            {data.map((_, i) => <Cell key={i} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─── Org trend — shipped+WIP per month, with past/current/future styling ────

export function OrgTrendChart({
  data,
}: {
  data: Array<{
    attribution_month: string;
    shipped: number;
    wip: number;
    isCurrent: boolean;
    isFuture: boolean;
  }>;
}) {
  const chartData = data.map((d) => ({
    month: d.attribution_month.slice(5),  // "2026-03" → "03" (short form)
    monthLabel: d.attribution_month,
    Shipped: Math.round(d.shipped),
    // Future months: WIP shown at reduced opacity via a separate key so the
    // legend and colouring remain unambiguous. Past-month WIP is "overdue".
    WIP: Math.round(d.wip),
    _current: d.isCurrent,
    _future: d.isFuture,
  }));

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={chartData} margin={{ top: 20, right: 10, left: 0, bottom: 0 }}>
        <CartesianGrid stroke={CHART_COLORS.grid} vertical={false} />
        <XAxis dataKey="month" tick={{ fontSize: 10, fill: CHART_COLORS.axis }} stroke={CHART_COLORS.grid} />
        <YAxis tick={{ fontSize: 10, fill: CHART_COLORS.axis }} stroke={CHART_COLORS.grid} tickFormatter={(v) => `$${Math.round(Number(v) / 1000)}k`} />
        <Tooltip content={<CurrencyTooltip />} />
        <Legend wrapperStyle={{ fontSize: 10 }} />
        <Bar dataKey="Shipped" stackId="v" fill={CHART_COLORS.shipped}>
          {chartData.map((_, i) => (
            <Cell
              key={i}
              fill={chartData[i]._current ? 'var(--color-brand-red)' : CHART_COLORS.shipped}
              fillOpacity={chartData[i]._future ? 0.5 : 1}
            />
          ))}
        </Bar>
        <Bar dataKey="WIP" stackId="v" fill={CHART_COLORS.wip}>
          {chartData.map((_, i) => (
            <Cell
              key={i}
              fill={CHART_COLORS.wip}
              fillOpacity={chartData[i]._future ? 0.6 : 1}
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

// ─── Deliverable vs Overhead stacked bar (org/team breakdown) ────────────────

export function TypeStackedBar({
  data,
}: {
  data: Array<{ month: string; deliverable: number; overhead: number }>;
}) {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={{ top: 8, right: 10, left: 0, bottom: 0 }}>
        <CartesianGrid stroke={CHART_COLORS.grid} vertical={false} />
        <XAxis dataKey="month" tick={{ fontSize: 10, fill: CHART_COLORS.axis }} stroke={CHART_COLORS.grid} />
        <YAxis tick={{ fontSize: 10, fill: CHART_COLORS.axis }} stroke={CHART_COLORS.grid} tickFormatter={(v) => `$${Math.round(Number(v) / 1000)}k`} />
        <Tooltip content={<CurrencyTooltip />} />
        <Legend wrapperStyle={{ fontSize: 10 }} />
        <Bar dataKey="deliverable" name="Deliverables" stackId="v" fill={CHART_COLORS.deliverable} />
        <Bar dataKey="overhead"    name="Overheads"    stackId="v" fill={CHART_COLORS.overhead} />
      </BarChart>
    </ResponsiveContainer>
  );
}

// ─── Forecast bar — actual + projected by month ──────────────────────────────

export function ForecastChart({
  data,
}: {
  data: Array<{ month: string; actual: number; projected: number }>;
}) {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={{ top: 8, right: 10, left: 0, bottom: 0 }}>
        <CartesianGrid stroke={CHART_COLORS.grid} vertical={false} />
        <XAxis dataKey="month" tick={{ fontSize: 10, fill: CHART_COLORS.axis }} stroke={CHART_COLORS.grid} />
        <YAxis tick={{ fontSize: 10, fill: CHART_COLORS.axis }} stroke={CHART_COLORS.grid} tickFormatter={(v) => `$${Math.round(Number(v) / 1000)}k`} />
        <Tooltip content={<CurrencyTooltip />} />
        <Legend wrapperStyle={{ fontSize: 10 }} />
        <Bar dataKey="actual"    name="Shipped"   fill={CHART_COLORS.shipped} radius={[3, 3, 0, 0]} />
        <Bar dataKey="projected" name="Projected" fill={CHART_COLORS.wip}     radius={[3, 3, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

// ─── Single-line trend (e.g., employees-on-tier over time) ───────────────────

export function TrendLine({
  data, dataKey, label,
}: {
  data: Array<{ month: string; [k: string]: string | number }>;
  dataKey: string;
  label: string;
}) {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={data} margin={{ top: 8, right: 10, left: 0, bottom: 0 }}>
        <CartesianGrid stroke={CHART_COLORS.grid} vertical={false} />
        <XAxis dataKey="month" tick={{ fontSize: 10, fill: CHART_COLORS.axis }} stroke={CHART_COLORS.grid} />
        <YAxis tick={{ fontSize: 10, fill: CHART_COLORS.axis }} stroke={CHART_COLORS.grid} />
        <Tooltip />
        <Line type="monotone" name={label} dataKey={dataKey} stroke={CHART_COLORS.tier_line} strokeWidth={2} dot={{ r: 3 }} />
      </LineChart>
    </ResponsiveContainer>
  );
}
